import { useCallback, useEffect, useState } from 'react';
import { assignmentsApi, type Assignment } from '@/lib/api';
import { DASHBOARD_PAGE_SIZE } from '@/components/PaginatedListFooter';
import { toast } from 'sonner';

function parsePaginated<T>(data: { results?: T[]; count?: number } | T[]): { items: T[]; total: number } {
  if (Array.isArray(data)) return { items: data, total: data.length };
  return { items: data.results ?? [], total: data.count ?? 0 };
}

export function useAssignments(search?: string, page = 1) {
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ pending: 0, inReview: 0, submitted: 0 });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const baseParams: Record<string, string> = {
        page: String(page),
        page_size: String(DASHBOARD_PAGE_SIZE),
      };
      if (search) baseParams.search = search;

      const countBase: Record<string, string> = { page_size: '1' };
      if (search) countBase.search = search;

      const [
        { data: listData },
        { data: pendingData },
        { data: inReviewData },
        { data: historyStats },
      ] = await Promise.all([
        assignmentsApi.list(baseParams),
        assignmentsApi.list({ ...countBase, status: 'pending' }),
        assignmentsApi.list({ ...countBase, status: 'in_review' }),
        // Submitted count is durable history (survives sample deletion).
        assignmentsApi.historyStats(),
      ]);

      const parsed = parsePaginated(listData);
      setAssignments(parsed.items);
      setTotal(parsed.total);
      setStatusCounts({
        pending: parsePaginated(pendingData).total,
        inReview: parsePaginated(inReviewData).total,
        submitted: historyStats.submitted,
      });
    } catch {
      toast.error('Failed to load assignments');
      setAssignments([]);
      setTotal(0);
      setStatusCounts({ pending: 0, inReview: 0, submitted: 0 });
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { assignments, total, statusCounts, loading, refresh, pageSize: DASHBOARD_PAGE_SIZE };
}
